import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchQuests } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import type { Quest, QuestSection } from '@/lib/types';

/**
 * Fetch quest catalogue. When the viewer is NOT authenticated, every quest is
 * forced to `status: 'locked'` so visitors can browse the list but can't claim
 * or interact. Wallet-gated everywhere.
 */
export function useQuests() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const query = useQuery<QuestSection[]>({
    queryKey: ['quests'],
    queryFn: fetchQuests,
    staleTime: 30_000,
  });

  const data = useMemo<QuestSection[] | undefined>(() => {
    if (!query.data) return query.data;
    if (isAuthenticated) return query.data;

    return query.data.map((section) => ({
      ...section,
      // `coming_soon` is a stronger statement than `locked` (no one can claim,
      // ever, until the backend ships the verifier) so we preserve it for
      // visitors instead of downgrading every quest to `locked`.
      quests: section.quests.map<Quest>((q) => ({
        ...q,
        status: q.status === 'coming_soon' ? 'coming_soon' : 'locked',
      })),
    }));
  }, [query.data, isAuthenticated]);

  return { ...query, data };
}
